import { useApolloClient, useMutation } from "@apollo/client";
import type { FetchResult } from "@apollo/client";
import { useAbility } from "@casl/react";
import { AppliedFilters, Table, useModal } from "@fluidattacks/design";
import _ from "lodash";
import { StrictMode, useCallback, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ActionButtons } from "./action-buttons";
import { AdditionModal } from "./addition-modal";
import { EditionModal } from "./edition-modal";
import { useToeLinesTable } from "./hooks/use-table-toe-lines";
import { useToeLinesPagination } from "./hooks/use-toe-lines-data";
import { useToeLinesPermissions } from "./hooks/use-toe-lines-permissions";
import { LocModal } from "./loc-modal";
import { GET_TOE_LINES, VERIFY_TOE_LINES } from "./queries";
import { SortsSuggestionsModal } from "./sorts-suggestions-modal";
import type {
  IGroupToeLinesProps,
  ISortsSuggestionAttr,
  IToeLinesAttr,
  IToeLinesConnection,
  IToeLinesData,
  IToeLinesEdge,
  IVerifyToeLinesResultAttr,
} from "./types";
import {
  formatToeLines,
  getUpdatedEdges,
  handleVerifyTOELinesError,
} from "./utils";

import { authzPermissionsContext } from "context/authz/config";
import { useToELinesFilters } from "features/toes-lines/filters";
import { getColumns } from "features/toes-lines/table/utils";
import type {
  CsvReportFiltersInput,
  GetToeLinesQueryVariables,
  LinesSortInput,
  ToeLinesFiltersInput,
  VerifyToeLinesMutation,
} from "gql/graphql";
import { useAuthz } from "hooks";
import { getErrors } from "utils/helpers";
import { Logger } from "utils/logger";
import { msgSuccess } from "utils/notifications";
import { useTableServerSideStore } from "utils/tables";

const TOE_LINES_THRESHOLD = 10;

const GroupToeLines: FC<IGroupToeLinesProps> = ({
  isInternal,
}): JSX.Element => {
  const { t } = useTranslation();
  const { canMutate } = useAuthz();
  const { groupName } = useParams() as { groupName: string };
  const client = useApolloClient();
  const tableHelpers = useTableServerSideStore();
  const permissions = useAbility(authzPermissionsContext);
  const canUpdateAttackedLines = canMutate("update_toe_lines_attacked_lines");
  const toeLinesPermissionsRaw = useToeLinesPermissions(groupName);
  const canSeeCoverage = permissions.can("see_toe_lines_coverage");
  const canSeeDaysToAttack = permissions.can("see_toe_lines_days_to_attack");
  const additionModalProps = useModal("addition-modal");
  const editionModalProps = useModal("edition-modal");
  const sortsSuggestionModalProps = useModal("sorts-suggestion-modal");
  const [isLocModalOpen, setIsLocModalOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [toeLinesFilters, setToeLinesFilters] = useState<ToeLinesFiltersInput>(
    {},
  );
  const [
    selectedToeLinesSortsSuggestions,
    setSelectedToeLinesSortsSuggestions,
  ] = useState<ISortsSuggestionAttr[]>();
  const { Filters, options, removeFilter } = useToELinesFilters({
    groupName,
    isInternal,
    setBackFilters: setToeLinesFilters,
  });

  const [handleVerifyToeLines] = useMutation(VERIFY_TOE_LINES, {
    onError: handleVerifyTOELinesError,
  });
  const toeLinesPermissions = useMemo((): GetToeLinesQueryVariables => {
    return {
      canGetAttackedAt: toeLinesPermissionsRaw.canGetAttackedAt,
      canGetAttackedBy: toeLinesPermissionsRaw.canGetAttackedBy,
      canGetAttackedLines: toeLinesPermissionsRaw.canGetAttackedLines,
      canGetBePresentUntil: toeLinesPermissionsRaw.canGetBePresentUntil,
      canGetComments: toeLinesPermissionsRaw.canGetComments,
      canGetFirstAttackAt: toeLinesPermissionsRaw.canGetFirstAttackAt,
      canGetNeedsReview: toeLinesPermissionsRaw.canGetNeedsReview,
      groupName: toeLinesPermissionsRaw.groupName,
    };
  }, [
    toeLinesPermissionsRaw.canGetAttackedAt,
    toeLinesPermissionsRaw.canGetAttackedBy,
    toeLinesPermissionsRaw.canGetAttackedLines,
    toeLinesPermissionsRaw.canGetBePresentUntil,
    toeLinesPermissionsRaw.canGetComments,
    toeLinesPermissionsRaw.canGetFirstAttackAt,
    toeLinesPermissionsRaw.canGetNeedsReview,
    toeLinesPermissionsRaw.groupName,
  ]);

  const { data, handleNextPage, handleSearch, refetch } = useToeLinesPagination(
    {
      variables: {
        ...toeLinesPermissions,
        filters: toeLinesFilters,
        first: 150,
        sortBy: tableHelpers.sortBy as unknown as LinesSortInput,
      },
    },
  );
  const hasActiveFilters = Object.values(toeLinesFilters).some(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (value): boolean => value !== undefined && value !== "",
  );

  const size = data?.group.toeLinesConnection.total;
  const toeLines = useMemo((): IToeLinesData[] => {
    const toeLinesEdges =
      data === undefined ? [] : data.group.toeLinesConnection.edges;

    return formatToeLines(toeLinesEdges);
  }, [data]);
  const handleUpdateAttackedLines = async (
    rootId: string,
    filename: string,
    comments: string,
    attackedLines: number,
  ): Promise<void> => {
    const result = await handleVerifyToeLines({
      variables: {
        ...toeLinesPermissions,
        attackedLines,
        comments,
        filename,
        groupName,
        rootId,
        shouldGetNewToeLines: true,
      },
    });
    if (
      !_.isNil(result.data) &&
      result.data.updateToeLinesAttackedLines.success
    ) {
      msgSuccess(
        t("group.toe.lines.alerts.verifyToeLines.success"),
        t("groupAlerts.updatedTitle"),
      );
      const updatedToeLines = result.data.updateToeLinesAttackedLines
        .toeLines as IToeLinesAttr;

      if (!_.isUndefined(updatedToeLines)) {
        if (data?.group) {
          client.writeQuery({
            data: {
              ...data,
              group: {
                ...data.group,
                toeLinesConnection: {
                  ...data.group.toeLinesConnection,
                  edges: data.group.toeLinesConnection.edges.map(
                    (value): IToeLinesEdge =>
                      value.node.root.id === rootId &&
                      value.node.filename === filename
                        ? { node: updatedToeLines }
                        : { node: value.node },
                  ),
                  total: data.group.toeLinesConnection.total as number | null,
                },
              },
            },
            query: GET_TOE_LINES,
            variables: {
              ...toeLinesFilters,
              ...toeLinesPermissions,
              first: 150,
            },
          });
        }
      }
    }
  };

  const toggleAdd = useCallback((): void => {
    additionModalProps.open();
  }, [additionModalProps]);
  const toggleEdit = useCallback((): void => {
    editionModalProps.open();
  }, [editionModalProps]);
  const toggleLocModal = useCallback((): void => {
    setIsLocModalOpen((prev): boolean => !prev);
  }, []);
  const updateQuery = useCallback(
    (
      updatedEdges: IToeLinesEdge[],
      currentData:
        | {
            group: {
              __typename: "Group";
              name: string;
              toeLinesConnection: IToeLinesConnection;
            };
          }
        | undefined,
    ): void => {
      if (currentData) {
        client.writeQuery({
          data: {
            ...currentData,
            group: {
              ...currentData.group,
              toeLinesConnection: {
                ...currentData.group.toeLinesConnection,
                edges: updatedEdges,
                total: currentData.group.toeLinesConnection.total ?? null,
              },
            },
          },
          query: GET_TOE_LINES,
          variables: { ...toeLinesFilters, ...toeLinesPermissions, first: 150 },
        });
      }
    },
    [client, toeLinesFilters, toeLinesPermissions],
  );
  const tableColumns = getColumns({
    canSeeCoverage,
    canSeeDaysToAttack,
    canUpdateAttackedLines,
    handleUpdateAttackedLines,
    isInternal,
    permissions,
    setIsSortsSuggestionsModalOpen: sortsSuggestionModalProps.setIsOpen,
    setSelectedToeLinesSortsSuggestions,
    toggleLocModal,
  });

  const { table } = useToeLinesTable({
    canUpdateAttackedLines,
    handleNextPage,
    handleSearch,
    isInternal,
    loading: _.isEmpty(toeLines) && size !== 0,
    size,
    tableColumns,
    tableHelpers,
    toeLines,
  });

  const { rowSelection } = table.getState();
  const { data: tableData } = table.options.meta ?? {};
  const selectedToeLines: IToeLinesData[] = Object.keys(rowSelection).map(
    (key): IToeLinesData => tableData?.get(key) ?? ({} as IToeLinesData),
  );
  const clearSelectedToeLines = useCallback((): void => {
    table.setRowSelection({});
  }, [table]);

  const handleOnVerifyCompleted = useCallback(
    async (
      results: FetchResult<IVerifyToeLinesResultAttr>[],
    ): Promise<void> => {
      const currentEdges = data?.group.toeLinesConnection.edges ?? [];
      const updatedEdges = results.reduce(
        (
          previousValue: IToeLinesEdge[],
          currentValue: FetchResult<IVerifyToeLinesResultAttr>,
        ): IToeLinesEdge[] => {
          const currentUpdated = selectedToeLines.map(
            (toeLine): IToeLinesEdge[] | undefined =>
              getUpdatedEdges(previousValue, currentValue, toeLine),
          );
          const filteredUpdated = currentUpdated.filter(
            (value): boolean => value !== undefined,
          );

          return _.last(filteredUpdated) ?? previousValue;
        },
        currentEdges,
      );
      updateQuery(
        updatedEdges.length === currentEdges.length
          ? updatedEdges
          : currentEdges,
        data,
      );
      if (
        !_.isNil(results[0].data) &&
        results[0].data.updateToeLinesAttackedLines.success
      ) {
        msgSuccess(
          t("group.toe.lines.alerts.verifyToeLines.success"),
          t("groupAlerts.updatedTitle"),
        );
        if (selectedToeLines.length >= TOE_LINES_THRESHOLD) await refetch();
        table.setRowSelection({});
      }
    },
    [data, refetch, selectedToeLines, t, table, updateQuery],
  );
  const handleVerify = useCallback((): void => {
    setIsVerifying(true);
    void Promise.all(
      selectedToeLines.map(
        async (toeInputData): Promise<FetchResult<VerifyToeLinesMutation>> =>
          handleVerifyToeLines({
            variables: {
              ...toeLinesPermissions,
              attackedLines: toeInputData.loc,
              comments: toeInputData.comments,
              filename: toeInputData.filename,
              groupName,
              rootId: toeInputData.rootId,
              shouldGetNewToeLines:
                selectedToeLines.length < TOE_LINES_THRESHOLD,
            },
          }),
      ),
    ).then((results: FetchResult<VerifyToeLinesMutation>[]): void => {
      const errors = getErrors<VerifyToeLinesMutation>(results);
      if (!_.isEmpty(results) && _.isEmpty(errors)) {
        void handleOnVerifyCompleted(
          results as FetchResult<IVerifyToeLinesResultAttr>[],
        );
      } else {
        refetch().catch((): void => {
          Logger.error("An error occurred formatting");
        });
      }
    });
    setIsVerifying(false);
  }, [
    selectedToeLines,
    handleVerifyToeLines,
    toeLinesPermissions,
    groupName,
    handleOnVerifyCompleted,
    refetch,
  ]);

  const csvFilters: CsvReportFiltersInput = {
    toeLinesFilters,
  };

  return (
    <StrictMode>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Filters />
            <Table.Columns table={table} />
            <ActionButtons
              filters={csvFilters}
              isAdding={additionModalProps.isOpen}
              isEditing={editionModalProps.isOpen}
              isFiltered={hasActiveFilters && Boolean(size)}
              isInternal={isInternal}
              isVerifying={isVerifying}
              onAdd={toggleAdd}
              onEdit={toggleEdit}
              onVerify={handleVerify}
              selectedToeLinesData={selectedToeLines}
            />
          </Table.Controls>
        </Table.Options>
        <Table.Caption>
          <AppliedFilters onClose={removeFilter} options={options} />
        </Table.Caption>
        <Table.Content />
        <Table.Footer />
      </Table>
      <AdditionModal
        groupName={groupName}
        modalRef={additionModalProps}
        refetchData={refetch}
      />
      <EditionModal
        clearSelectedToeLines={clearSelectedToeLines}
        groupName={groupName}
        modalRef={editionModalProps}
        refetchData={refetch}
        selectedToeLinesData={selectedToeLines}
      />
      {isLocModalOpen ? (
        <LocModal
          clearSelectedToeLines={clearSelectedToeLines}
          groupName={groupName}
          handleCloseModal={toggleLocModal}
          refetchData={refetch}
          selectedToeLineData={selectedToeLines[0]}
        />
      ) : undefined}
      <SortsSuggestionsModal
        modalRef={sortsSuggestionModalProps}
        selectedSortsSuggestions={selectedToeLinesSortsSuggestions}
      />
    </StrictMode>
  );
};
export { GroupToeLines };
